"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "./utils";

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 0,
  hideArrow = false,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  hideArrow?: boolean;
}) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          // `text-wrap` (not `text-balance`): callers cap the bubble with a
          // `max-w-*`, and balancing splits the text into even-length lines
          // that stop well short of that cap, leaving the bubble padded out
          // with empty space. Normal wrapping fills each line, so the bubble
          // shrinks to the width the text actually needs.
          "bg-white text-[#0f172a] dark:bg-black dark:text-white shadow-lg ring-1 ring-black/10 dark:ring-white/15 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-wrap",
          className,
        )}
        {...props}
      >
        {children}
        {!hideArrow && (
          <TooltipPrimitive.Arrow className="fill-white dark:fill-black z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
        )}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

/**
 * Convenience wrapper that replaces native `title` hover tooltips with this
 * styled tooltip. The trigger is wrapped in an inline-flex span so the tooltip
 * still appears even when the child button is `disabled` (disabled elements
 * don't emit pointer events, so Radix can't bind to them directly).
 */
function Tip({
  label,
  children,
  side = "bottom",
  align = "center",
  collisionPadding = 8,
  className = "inline-flex",
  contentClassName,
  overflowOnly = false,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  /**
   * Where the bubble sits along the trigger's edge. "center" suits an icon
   * button; use "start" when the trigger is a wide block (a card title filling
   * its row) so the bubble lines up with the text instead of straddling it.
   */
  align?: "start" | "center" | "end";
  /** Keeps the bubble this far from the viewport edge before it flips/shifts. */
  collisionPadding?: number;
  /** Class applied to the wrapper span (override to preserve layout, e.g. flex-1 flex). */
  className?: string;
  /** Class applied to the tooltip bubble itself — use for max-width / word-break. */
  contentClassName?: string;
  /** If true, the tooltip will only show if the text inside the wrapper is overflowing/truncated. */
  overflowOnly?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLSpanElement>(null);

  if (!label) return <>{children}</>;

  return (
    <Tooltip
      open={overflowOnly ? open : undefined}
      onOpenChange={(next) => {
        if (!overflowOnly) return;
        if (!next) {
          setOpen(false);
          return;
        }
        const el = triggerRef.current;
        if (el) {
          const checkOverflow = (n: Element) => n.scrollWidth > n.clientWidth + 1 || n.scrollHeight > n.clientHeight + 1;
          const hasOverflow = checkOverflow(el) || Array.from(el.querySelectorAll("*")).some(checkOverflow);
          setOpen(hasOverflow);
        }
      }}
    >
      <TooltipTrigger asChild>
        <span ref={triggerRef} className={className}>{children}</span>
      </TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        sideOffset={6}
        collisionPadding={collisionPadding}
        className={contentClassName}
      >
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, Tip };
